import { Events } from "@events";

class WebRTCRecorder {
  /**
   * @type {import("./webrtcRoom.js").default} room
   */
  room = null;

  peerChangeRotationFunctions = {};

  callEnded = false;

  disconnectAlerted = false;
  disconnectAlertLogged = false;

  constructor(room, options) {
    this.room = room;
    this.options = options;
    console.log("WebRTCRecorder: initializing for agent");

    window.recorderErrorTrigger = (message) => {
      if (this.disconnectAlerted && !this.disconnectAlertLogged) {
        this.disconnectAlertLogged = true;
        console.log("WebRTCRecorder: recorder error ignored due to disconnect alert: " + message);
        return;
      }
      console.error("WebRTCRecorder: recorder error: " + message);
      this.room.callEventEmitter.emit(Events.RecordingFailed, { message: message });
      this.room.state.addConnectionLog("RECORDER-ERROR: " + message);
    };

    window.recorderTimestampCallback = (rtcId, tsOffsetMs) => {
      console.log("WebRTCRecorder: received _ts offset from WDE: " + tsOffsetMs + "ms for " + rtcId);
      this.room.callEventEmitter.emit(Events.RecordingTimestampOffset, { tsOffsetMs });
    };

    this.room.callEventEmitter.on("webrtc-ice-disconnect-popup-show", () => {
      if (!this.disconnectAlerted) {
        this.disconnectAlerted = true;
        this.disconnectAlertLogged = false;
      }
    });

    this.room.callEventEmitter.on("webrtc-ice-disconnect-popup-hide", () => {
      if (this.disconnectAlerted) {
        this.disconnectAlerted = false;
        this.disconnectAlertLogged = false;
      }
    });

    if (this.room.state.role !== "agent") {
      console.log("WebRTCRecorder: not agent, not recording. role: " + this.room.state.role);
      return;
    }

    let localStreamPromiseResolve = null;
    this.localStreamPromise = new Promise((resolve) => {
      localStreamPromiseResolve = resolve;
    });

    let remoteStreamPromiseResolve = null;
    this.remoteStreamPromise = new Promise((resolve) => {
      remoteStreamPromiseResolve = resolve;
    });

    let rtcConnectionPromiseResolve = null;
    this.rtcConnectionPromise = new Promise((resolve) => {
      rtcConnectionPromiseResolve = resolve;
    });

    this.room.callEventEmitter.on(Events.OnLocalStream, (stream) => {
      this.localStream = stream;
      console.log("WebRTCRecorder: local stream ready");
      localStreamPromiseResolve(stream);
    });

    this.room.callEventEmitter.on(Events.OnRemoteStream, (stream) => {
      this.remoteStream = stream;
      console.log("WebRTCRecorder: remote stream ready");
      remoteStreamPromiseResolve(stream);
    });

    this.room.callEventEmitter.on(Events.WebRTCConnected, (stream) => {
      this.remoteStream = stream;
      console.log("WebRTCRecorder: rtc connected");
      rtcConnectionPromiseResolve(stream);
    });

    (async () => {
      while (true) {
        try {
          var res = await this.waitRecording();
          if (!res) {
            console.error("WebRTCRecorder: error waiting for recording");

            // check urlparams if test is true do not retry
            const urlParams = new URLSearchParams(window.location.search);
            const testParam = urlParams.get("test");
            if (testParam && testParam.toLowerCase() === "true") {
              console.log("WebRTCRecorder: test mode, not retrying");
              return;
            }
            await new Promise((resolve) => setTimeout(resolve, 2000));
            continue;
          }
          return;
        } catch {
          console.error("WebRTCRecorder: error waiting for recording");
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }
    })();
  }

  async waitRecording() {
    //await window?.CefSharp?.BindObjectAsync("msr");
    var transportInitPromise = null;

    if (window.ClientTransport && window.ClientTransport.initialize) {
      transportInitPromise = window.ClientTransport.initialize();
    }

    console.log("WebRTCRecorder: waiting for streams");

    await this.localStreamPromise;
    await this.remoteStreamPromise;

    console.log("WebRTCRecorder: streams ready");

    await this.rtcConnectionPromise;
    console.log("WebRTCRecorder: rtc connection ready, trying to start recording");

    const searchParams = new URLSearchParams(window.location.search);
    const isTest = searchParams.get("test");

    // Initialize transport layer (CEF scheme → WebSocket → legacy msr binding).
    // This does NOT depend on window.msr — it probes CEF scheme and WS ports directly.
    var transport = null;
    if (transportInitPromise) transport = await transportInitPromise;

    // If no modern transport found, try browser-side recording fallback
    if (!transport) {
      this.room.state.addConnectionLog("NO-RELAY-RECORDING-TRANSPORT");
      console.warn("WebRTCRecorder: no modern transport available, trying browser recording fallback");

      if (window.BrowserRecordingClient && window.RelayMultistreamRecorder && window.RelayMultistreamRecorder.initFallback) {
        var vpBaseUrl = window.CONFIG && window.CONFIG.LOCAL_URL_BASE !== undefined ? window.CONFIG.LOCAL_URL_BASE : null;
        var agentUsername = new URLSearchParams(window.location.search).get("agent");

        if (vpBaseUrl !== null && agentUsername) {
          console.log("WebRTCRecorder: No C# transport, trying browser recording fallback");
          this.room.state.addConnectionLog("PROBING-BROWSER-RECORDING");

          var fallbackOk = await window.RelayMultistreamRecorder.initFallback(agentUsername, vpBaseUrl);
          if (fallbackOk) {
            console.log("WebRTCRecorder: Browser recording fallback active");
            this.room.state.addConnectionLog("BROWSER-RECORDING-ACTIVE");
            this.startRecording();
            return true;
          }
        } else {
          console.warn(
            "WebRTCRecorder: Browser recording fallback not possible — missing",
            vpBaseUrl === null ? "LOCAL_URL_BASE" : "agent query param",
          );
        }
      }

      if (!isTest) {
        this.room.callEventEmitter.emit(Events.RecordingFailed, {
          message: "No recording transport available (no CEF scheme, no WebSocket, no msr)",
        });
        console.error("WebRTCRecorder: No recording transport available");
        this.room.state.addConnectionLog("NO-RECORDING-TRANSPORT");
      }
      return false;
    }

    this.startRecording();
    return true;
  }

  async startRecording() {
    // add 1 to window.startedSessions for each recording start
    if (!window.startedSessions) {
      window.startedSessions = 0;
    }
    window.startedSessions += 1;
    console.log("WebRTCRecorder: starting recording, total started sessions: " + window.startedSessions);
    if (this.callEnded) {
      console.log("WebRTCRecorder: call ended, not starting recording");
      return;
    }

    this.room.callEventEmitter.on(Events.VideoCallEnded, this.stopRecording.bind(this));
    this.room.callEventEmitter.emit(Events.RecordingStarted);

    console.log("WebRTCRecorder: starting recording");
    for (const peer of this.room.state.peers) {
      console.log("WebRTCRecorder: recording peer " + peer.type);

      if (this.options && this.options.whiteListPeers && !this.options.whiteListPeers.includes(peer.type)) {
        console.log("WebRTCRecorder: skipping peer " + peer.type + " due to whitelist");
        continue;
      }

      if (peer.videoElementReadyPromise) {
        console.log("WebRTCRecorder: waiting for video element ready for peer " + peer.peerName);
        await peer.videoElementReadyPromise;
      }

      var peerType = peer.type;
      if (this.options && this.options.peerTypeOverrides && this.options.peerTypeOverrides[peer.type]) {
        peerType = this.options.peerTypeOverrides[peer.type];
        console.log(`WebRTCRecorder: overriding peer type for ${peer.type} to ${peerType}`);
      }

      var rtcId = this.room.rtcId;
      var interactionId = this.room.state.getInteractionId();
      if (this.options && this.options.overrideRtcId) {
        rtcId = this.options.overrideRtcId;
        console.log(`WebRTCRecorder: overriding rtcId to ${rtcId}`);
      }
      if (this.options && this.options.overrideInteractionId) {
        interactionId = this.options.overrideInteractionId;
        console.log(`WebRTCRecorder: overriding interactionId to ${interactionId}`);
      }

      this.peerChangeRotationFunctions[peer.peerName] = await window.RelayMultistreamRecorder.recordVideoElement(
        peer.video,
        peerType,
        rtcId,
        interactionId,
        0,
      );

      // add delay between starting recordings to avoid overload
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  async stopRecording() {
    if (this.callEnded) {
      console.log("WebRTCRecorder: stopRecording already called, ignoring duplicate");
      return;
    }
    this.callEnded = true;

    console.log("WebRTCRecorder: stopping recording");
    var rtcId = this.room.rtcId;
    var interactionId = this.room.state.getInteractionId();

    if (this.options && this.options.overrideRtcId) {
      rtcId = this.options.overrideRtcId;
      console.log(`WebRTCRecorder: overriding rtcId to ${rtcId}`);
    }

    if (this.options && this.options.overrideInteractionId) {
      interactionId = this.options.overrideInteractionId;
      console.log(`WebRTCRecorder: overriding interactionId to ${interactionId}`);
    }
    for (const peer of this.room.state.peers) {
      console.log("WebRTCRecorder: recording peer " + peer.type);

      if (this.options && this.options.whiteListPeers && !this.options.whiteListPeers.includes(peer.type)) {
        console.log("WebRTCRecorder: skipping peer " + peer.type + " due to whitelist");
        continue;
      }

      if (peer.videoElementReadyPromise) {
        console.log("WebRTCRecorder: waiting for video element ready for peer " + peer.peerName);
        await peer.videoElementReadyPromise;
      }

      window.RelayMultistreamRecorder.stopRecordingVideoElement(peer.video);
    }


    //  substract 1 from window.startedSessions for each recording stop
    if (window.startedSessions) {
      window.startedSessions -= 1;
    }
    console.log("WebRTCRecorder: recording stopped, total started sessions: " + window.startedSessions);

    if (window.startedSessions <= 0) {
      console.log("WebRTCRecorder: all recordings stopped, calling callEnded on recorder");
      window.RelayMultistreamRecorder.callEnded(rtcId, interactionId);
    }
  }
}

export { WebRTCRecorder };
